"use client";

import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useUniversalAuthOptional } from "@west-tennessee-consulting/ui";
import {
  Home,
  Ticket,
  Clock,
  Users,
  BarChart3,
  Search,
  ArrowLeft,
  AlertTriangle,
} from "lucide-react";

export default function NotFound() {
  // Boundary page: may render outside the provider tree (e.g. a 404 from a
  // stale Server Action POST). Use the non-throwing variant so the 404 page
  // never crashes when SimpleAuthProvider isn't mounted.
  const { isClientUser, isEmployee, isAuthenticated } = useUniversalAuthOptional();

  const getQuickActions = () => {
    if (!isAuthenticated) {
      return [
        {
          title: "Employee Portal",
          description: "Access the full MSP ticketing system",
          href: "/auth/employee",
          icon: Ticket,
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-950/40",
        },
        {
          title: "Client Portal",
          description: "View your tickets and reports",
          href: "/auth/client",
          icon: Users,
          color: "text-green-600 dark:text-green-400",
          bgColor: "bg-green-50 dark:bg-green-950/40",
        },
      ];
    }

    if (isEmployee) {
      return [
        {
          title: "Dashboard",
          description: "Return to your main dashboard",
          href: "/dashboard",
          icon: Home,
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-950/40",
        },
        {
          title: "My Tickets",
          description: "View your assigned tickets",
          href: "/tickets/my-tickets",
          icon: Ticket,
          color: "text-orange-600 dark:text-orange-400",
          bgColor: "bg-orange-50 dark:bg-orange-950/40",
        },
        {
          title: "Log Time",
          description: "Quick time entry",
          href: "/time-tracking/log",
          icon: Clock,
          color: "text-green-600 dark:text-green-400",
          bgColor: "bg-green-50 dark:bg-green-950/40",
        },
        {
          title: "All Tickets",
          description: "View all system tickets",
          href: "/tickets/all-tickets",
          icon: Search,
          color: "text-purple-600 dark:text-purple-400",
          bgColor: "bg-purple-50 dark:bg-purple-950/40",
        },
      ];
    }

    // Client user
    if (isClientUser) {
      return [
        {
          title: "Contact Dashboard",
          description: "Return to your contact dashboard",
          href: "/contacts/dashboard",
          icon: Home,
          color: "text-blue-600 dark:text-blue-400",
          bgColor: "bg-blue-50 dark:bg-blue-950/40",
        },
        {
          title: "My Tickets",
          description: "View your company tickets",
          href: "/tickets/all-tickets",
          icon: Ticket,
          color: "text-orange-600 dark:text-orange-400",
          bgColor: "bg-orange-50 dark:bg-orange-950/40",
        },
        {
          title: "Time Reports",
          description: "View time tracking reports",
          href: "/contacts/reports",
          icon: BarChart3,
          color: "text-green-600 dark:text-green-400",
          bgColor: "bg-green-50 dark:bg-green-950/40",
        },
      ];
    }

    // Default fallback
    return [
      {
        title: "Home",
        description: "Return to the main page",
        href: "/",
        icon: Home,
        color: "text-blue-600 dark:text-blue-400",
        bgColor: "bg-blue-50 dark:bg-blue-950/40",
      },
    ];
  };

  const quickActions = getQuickActions();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-red-100 dark:bg-red-900/40 rounded-full w-16 h-16 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-3xl font-bold text-card-foreground">
              Page Not Found
            </CardTitle>
            <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
              The page you&apos;re looking for doesn&apos;t exist or has been moved.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Error Details */}
            <div className="text-center">
              <p className="text-gray-500 mb-4">
                Error 404 - The requested resource could not be found
              </p>
              <p className="text-sm text-gray-400">
                {isAuthenticated
                  ? `Welcome back, ${isEmployee ? "employee" : "contact"}!`
                  : "Please sign in to access the system."}
              </p>
            </div>

            {/* Quick Actions */}
            <div>
              <h3 className="text-lg font-semibold text-card-foreground mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quickActions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <Link key={index} href={action.href}>
                      <Card
                        className={`cursor-pointer transition-all duration-200 hover:shadow-md ${action.bgColor}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${action.bgColor}`}>
                              <Icon className={`h-5 w-5 ${action.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-card-foreground text-sm">
                                {action.title}
                              </h4>
                              <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{action.description}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Additional Help */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-card-foreground mb-4">Need Help?</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-card-50 rounded-lg">
                  <Search className="h-5 w-5 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-card-foreground">Check the URL</p>
                    <p className="text-xs text-gray-500">
                      Make sure the address is spelled correctly
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-card-50 rounded-lg">
                  <ArrowLeft className="h-5 w-5 text-gray-500" />
                  <div>
                    <p className="text-sm font-medium text-card-foreground">Go Back</p>
                    <p className="text-xs text-gray-500">
                      Use your browser&apos;s back button to return
                    </p>
                  </div>
                </div>

                {isAuthenticated && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-950/40 rounded-lg">
                    <Home className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Return to Dashboard</p>
                      <p className="text-xs text-blue-600 dark:text-blue-400">Start fresh from your main dashboard</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Support */}
            <div className="text-center pt-4 border-t">
              <p className="text-sm text-gray-500 mb-2">Still having trouble?</p>
              <div className="flex justify-center gap-4">
                <Link href="/">
                  <Button variant="outline" size="sm">
                    <Home className="h-4 w-4 mr-2" />
                    Go Home
                  </Button>
                </Link>
                <a href="mailto:support@wtc.com">
                  <Button variant="outline" size="sm">
                    Contact Support
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
